import React from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import useTooltip from './useTooltip';
import getStyles from './helpers/getStyles';
import getPosition from './helpers/getPosition';
import useRect, { RectResult } from '../helpers/useRect';
import parseClassnames from '../helpers/parseClassnames';
import { BaseTooltipProps } from './types';
import Text from '../Text';

interface TooltipContentProps extends BaseTooltipProps {
  isVisible: boolean;
  triggerRect: RectResult;
}

const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(
  (
    {
      label,
      position,
      setPosition = getPosition,
      isVisible,
      triggerRect,
      style,
      type = 'regular',
      className = '',
      ...rest
    },
    forwardedRef,
  ) => {
    const ownRef = React.useRef(null);
    const ref = forwardedRef || ownRef;
    const tooltipRect = useRect(ownRef, isVisible);

    return (
      <div
        role="tooltip"
        style={{
          ...style,
          ...getStyles(setPosition, triggerRect, tooltipRect, position),
        }}
        ref={ref}
        className={parseClassnames(
          className,
          cn(
            `bg-white shadow-tooltip absolute z-dialog pointer-events-none whitespace-no-wrap`,
            {
              'p-8': type === 'regular',
              'p-4': type === 'small',
            },
          ),
        )}
        {...rest}
      >
        <Text type="legals">{label}</Text>
      </div>
    );
  },
);

interface TooltipPopupProps extends BaseTooltipProps {
  isVisible: boolean;
}

const TooltipPopup = React.forwardRef<HTMLDivElement, TooltipPopupProps>(
  ({ label, position, isVisible, ...rest }, forwardedRef) => {
    return isVisible
      ? ReactDOM.createPortal(
          <TooltipContent
            label={label}
            position={position}
            isVisible={isVisible}
            ref={forwardedRef}
            {...rest}
          />,
          document.body,
        )
      : null;
  },
);

const Tooltip: React.FC<BaseTooltipProps> = ({
  label,
  children,
  DEBUG,
  ...rest
}) => {
  const child = React.Children.only(children);

  if (!React.isValidElement(child)) {
    throw new Error(
      "You must provide a valid child for the tooltip. Otherwise, there's no tooltip to show",
    );
  }

  const [trigger, tooltip] = useTooltip({
    onMouseEnter: child.props.onMouseEnter,
    onMouseMove: child.props.onMouseMove,
    onMouseLeave: child.props.onMouseLeave,
    onFocus: child.props.onFocus,
    onBlur: child.props.onBlur,
    onKeyDown: child.props.onKeyDown,
    onMouseDown: child.props.onMouseDown,
    ref: child.ref || null,
    DEBUG,
  });

  return (
    <>
      {React.cloneElement(child, trigger)}
      <TooltipPopup label={label} {...tooltip} {...rest} />
    </>
  );
};

export default Tooltip;
